import { z } from 'zod'

import { discounTypeSchema } from './discounTypes'

export const discountShopKeys = ['101', '201', '202', '203', '204', '205', '1001', '1002'] as const
export type DiscountShopKey = typeof discountShopKeys[number]

export const countdownVariantSchema = z.enum(['compact', 'banner', 'minimal'])
export type CountdownVariant = z.infer<typeof countdownVariantSchema>

export const discountShopSettingFields = [
  'slogan',
  'discountRule',
  'discountRuleStock',
  'limitStockDiscountToStock',
  'quantityDiscount',
  'type',
  'countdownVariant',
  'showPercent',
  'dynamicPrice',
  'dynamicDiscount',
  'dynamicDiscountStock',
  'dynamicMaxPrice',
  'dynamicMaxPriceStock',
  'exportToIdealo',
] as const

export const quantityDiscountRewardTypeSchema = z.enum(['freeItems', 'percent'])
export const quantityDiscountApplyToSchema = z.enum(['rewardItems', 'allItems'])

export const quantityDiscountSchema = z.object({
  enabled: z.boolean().default(false),
  requiredQuantity: z.number().int().positive().optional(),
  rewardType: quantityDiscountRewardTypeSchema.default('freeItems'),
  rewardQuantity: z.number().int().positive().optional(),
  rewardPercent: z.number().positive().max(100).optional(),
  applyTo: quantityDiscountApplyToSchema.default('rewardItems'),
  repeating: z.boolean().default(true),
}).superRefine((value, ctx) => {
  if (!value.enabled) return

  if (!value.requiredQuantity) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['requiredQuantity'],
      message: 'requiredQuantity is required for quantity discounts',
    })
  }

  if (value.rewardType === 'freeItems' && !value.rewardQuantity) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['rewardQuantity'],
      message: 'rewardQuantity is required for free item quantity discounts',
    })
  }

  if (value.rewardType === 'percent' && !value.rewardPercent) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['rewardPercent'],
      message: 'rewardPercent is required for percent quantity discounts',
    })
  }

  if (
    value.rewardType === 'percent'
    && value.applyTo === 'rewardItems'
    && !value.rewardQuantity
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['rewardQuantity'],
      message: 'rewardQuantity is required when percent quantity discounts target reward items',
    })
  }
})
export type QuantityDiscount = z.infer<typeof quantityDiscountSchema>

export const discountShopSettingsSchema = z.object({
  slogan: z.string().optional(),
  discountRule: z.string(),
  discountRuleStock: z.string(),
  limitStockDiscountToStock: z.boolean().default(false),
  quantityDiscount: quantityDiscountSchema.optional(),
  type: discounTypeSchema,
  countdownVariant: countdownVariantSchema.optional(),
  showPercent: z.boolean().default(true),
  dynamicPrice: z.boolean().default(false),
  dynamicDiscount: z.string().optional(),
  dynamicDiscountStock: z.string().optional(),
  // Legacy Feld: bleibt lesbar, wird aber nicht mehr fuer die Berechnung genutzt.
  dynamicMinPrice: z.string().optional(),
  dynamicMaxPrice: z.string().optional(),
  dynamicMaxPriceStock: z.string().optional(),
  exportToIdealo: z.boolean().default(true),
})
export type DiscountShopSettings = z.infer<typeof discountShopSettingsSchema>

export const discountShopOverrideSchema = discountShopSettingsSchema.partial()
export type DiscountShopOverride = z.infer<typeof discountShopOverrideSchema>

export const discountShopOverridesSchema = z.object({
  '101': discountShopOverrideSchema.optional(),
  '201': discountShopOverrideSchema.optional(),
  '202': discountShopOverrideSchema.optional(),
  '203': discountShopOverrideSchema.optional(),
  '204': discountShopOverrideSchema.optional(),
  '205': discountShopOverrideSchema.optional(),
  '1001': discountShopOverrideSchema.optional(),
  '1002': discountShopOverrideSchema.optional(),
})
export type DiscountShopOverrides = z.infer<typeof discountShopOverridesSchema>

export const appliedShopDiscountSchema = z.object({
  discountId: z.number(),
  discountPrice: z.number(),
  discountPriceAfterStock: z.number().optional(),
  stockDiscountLimit: z.number().optional(),
  limitStockDiscountToStock: z.boolean().optional(),
  quantityDiscount: quantityDiscountSchema.optional(),
  discountTitle: z.string().optional(),
  discountType: discounTypeSchema.optional(),
  countdownVariant: countdownVariantSchema.optional(),
  showPercent: z.boolean().optional(),
  discountDateStart: z.coerce.date().optional(),
  discountDateEnd: z.coerce.date().optional(),
  exportToIdealo: z.boolean().optional(),
})
export type AppliedShopDiscount = z.infer<typeof appliedShopDiscountSchema>

export const appliedShopDiscountsSchema = z.object({
  '101': appliedShopDiscountSchema.optional(),
  '201': appliedShopDiscountSchema.optional(),
  '202': appliedShopDiscountSchema.optional(),
  '203': appliedShopDiscountSchema.optional(),
  '204': appliedShopDiscountSchema.optional(),
  '205': appliedShopDiscountSchema.optional(),
  '1001': appliedShopDiscountSchema.optional(),
  '1002': appliedShopDiscountSchema.optional(),
})
export type AppliedShopDiscounts = z.infer<typeof appliedShopDiscountsSchema>

type DiscountConfigCarrier = DiscountShopSettings & {
  shopOverrides?: DiscountShopOverrides
}

type DiscountDateCarrier = {
  id: number
  dateStart?: Date
  dateEnd?: Date
}

type CombinationDiscountCarrier = {
  price: number
  idealo_bestpreis?: number
  shopDiscounts?: AppliedShopDiscounts
  discountId?: number
  discountPrice?: number
  discountPriceAfterStock?: number
  stockDiscountLimit?: number
  limitStockDiscountToStock?: boolean
  quantityDiscount?: QuantityDiscount
  discountTitle?: string
  discountType?: DiscountShopSettings['type']
  discountCountdownVariant?: CountdownVariant
  discountShowPercent?: boolean
  discountDateStart?: Date
  discountDateEnd?: Date
  discountShops?: number[]
}

export function isDiscountShopKey(value: string): value is DiscountShopKey {
  return discountShopKeys.indexOf(value as DiscountShopKey) >= 0
}

export function toDiscountShopKey(shopId: number): DiscountShopKey | undefined {
  const key = String(shopId)
  return isDiscountShopKey(key) ? key : undefined
}

export function resolveDiscountShopSettings(
  discount: DiscountConfigCarrier,
  shopId: number,
): DiscountShopSettings {
  const shopKey = toDiscountShopKey(shopId)
  const override = shopKey ? discount.shopOverrides?.[shopKey] : undefined

  return {
    slogan: override?.slogan ?? discount.slogan,
    discountRule: override?.discountRule ?? discount.discountRule,
    discountRuleStock: override?.discountRuleStock ?? discount.discountRuleStock,
    limitStockDiscountToStock: override?.limitStockDiscountToStock ?? discount.limitStockDiscountToStock ?? false,
    quantityDiscount: override?.quantityDiscount ?? discount.quantityDiscount,
    type: override?.type ?? discount.type,
    countdownVariant: override?.countdownVariant ?? discount.countdownVariant,
    showPercent: override?.showPercent ?? discount.showPercent ?? true,
    dynamicPrice: override?.dynamicPrice ?? discount.dynamicPrice ?? false,
    dynamicDiscount: override?.dynamicDiscount ?? discount.dynamicDiscount,
    dynamicDiscountStock: override?.dynamicDiscountStock ?? discount.dynamicDiscountStock,
    dynamicMaxPrice: override?.dynamicMaxPrice ?? discount.dynamicMaxPrice,
    dynamicMaxPriceStock: override?.dynamicMaxPriceStock ?? discount.dynamicMaxPriceStock,
    exportToIdealo: override?.exportToIdealo ?? discount.exportToIdealo ?? true,
  }
}

const normalizePositiveInteger = (value: unknown): number | undefined => {
  const numberValue = typeof value === 'number'
    ? value
    : Number(value)

  if (!Number.isFinite(numberValue)) return undefined

  const integerValue = Math.floor(numberValue)
  return integerValue > 0 ? integerValue : undefined
}

const normalizePositivePercent = (value: unknown): number | undefined => {
  const numberValue = typeof value === 'number'
    ? value
    : Number(value)

  if (!Number.isFinite(numberValue)) return undefined
  if (numberValue <= 0) return undefined

  return Math.min(100, numberValue)
}

export function normalizeQuantityDiscount(
  quantityDiscount?: QuantityDiscount | null,
): QuantityDiscount | undefined {
  if (!quantityDiscount?.enabled) return undefined

  const requiredQuantity = normalizePositiveInteger(quantityDiscount.requiredQuantity)
  if (!requiredQuantity) return undefined

  const rewardType = quantityDiscount.rewardType || 'freeItems'
  const applyTo = quantityDiscount.applyTo || (
    rewardType === 'percent'
      ? 'allItems'
      : 'rewardItems'
  )

  if (rewardType === 'freeItems') {
    const rewardQuantity = normalizePositiveInteger(quantityDiscount.rewardQuantity)
    if (!rewardQuantity) return undefined

    return {
      enabled: true,
      requiredQuantity,
      rewardType,
      rewardQuantity,
      applyTo: 'rewardItems',
      repeating: quantityDiscount.repeating ?? true,
    }
  }

  const rewardPercent = normalizePositivePercent(quantityDiscount.rewardPercent)
  if (!rewardPercent) return undefined

  const rewardQuantity = applyTo === 'rewardItems'
    ? normalizePositiveInteger(quantityDiscount.rewardQuantity)
    : undefined

  if (applyTo === 'rewardItems' && !rewardQuantity) return undefined

  return {
    enabled: true,
    requiredQuantity,
    rewardType,
    rewardPercent,
    ...(rewardQuantity ? { rewardQuantity } : {}),
    applyTo,
    repeating: quantityDiscount.repeating ?? true,
  }
}

type DiscountDateValue = Date | string | number | null | undefined

type QuantityDiscountAvailabilityContext = {
  onlyStock?: boolean | null
  stock?: number | null
  dateStart?: DiscountDateValue
  dateEnd?: DiscountDateValue
  now?: Date | number
}

const normalizeTimestamp = (value: DiscountDateValue): number | undefined => {
  if (value == null || value === '') return undefined
  if (value instanceof Date) {
    const timestamp = value.getTime()
    return Number.isFinite(timestamp) ? timestamp : undefined
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined

  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : undefined
}

const normalizeNowTimestamp = (now?: Date | number) => {
  if (now instanceof Date) return now.getTime()
  if (typeof now === 'number') return now
  return Date.now()
}

export function isDiscountDateActive({
  dateStart,
  dateEnd,
  now,
}: Pick<QuantityDiscountAvailabilityContext, 'dateStart' | 'dateEnd' | 'now'>): boolean {
  const nowTimestamp = normalizeNowTimestamp(now)
  const startTimestamp = normalizeTimestamp(dateStart)
  const endTimestamp = normalizeTimestamp(dateEnd)

  if (!Number.isFinite(nowTimestamp)) return true
  if (startTimestamp !== undefined && startTimestamp > nowTimestamp) return false
  if (endTimestamp !== undefined && endTimestamp <= nowTimestamp) return false

  return true
}

export function getQuantityDiscountTriggerQuantity(
  quantityDiscount?: QuantityDiscount | null,
): number {
  const normalizedQuantityDiscount = normalizeQuantityDiscount(quantityDiscount)
  if (!normalizedQuantityDiscount) return 0

  return normalizePositiveInteger(normalizedQuantityDiscount.requiredQuantity) || 0
}

export function hasSufficientStockForQuantityDiscount(
  quantityDiscount?: QuantityDiscount | null,
  stock?: number | null,
): boolean {
  const triggerQuantity = getQuantityDiscountTriggerQuantity(quantityDiscount)
  if (triggerQuantity <= 0) return false

  if (typeof stock !== 'number' || !Number.isFinite(stock)) return true

  return Math.max(0, Math.floor(stock)) >= triggerQuantity
}

export function isQuantityDiscountAvailable(
  quantityDiscount?: QuantityDiscount | null,
  context: QuantityDiscountAvailabilityContext = {},
): boolean {
  const normalizedQuantityDiscount = normalizeQuantityDiscount(quantityDiscount)
  if (!normalizedQuantityDiscount) return false

  if (!isDiscountDateActive(context)) return false

  if (
    context.onlyStock === true
    && !hasSufficientStockForQuantityDiscount(normalizedQuantityDiscount, context.stock)
  ) return false

  return true
}

export function shouldIgnoreQuantityDiscount(
  quantityDiscount?: QuantityDiscount | null,
  context: QuantityDiscountAvailabilityContext = {},
): boolean {
  return !isQuantityDiscountAvailable(quantityDiscount, context)
}

const isSameSettingValue = (
  left: DiscountShopOverride[keyof DiscountShopOverride],
  right: DiscountShopSettings[keyof DiscountShopSettings],
) => {
  if (left === right) return true
  if (left === undefined || right === undefined) return false

  if (typeof left === 'object' && typeof right === 'object') {
    return JSON.stringify(left) === JSON.stringify(right)
  }

  return false
}

export function normalizeDiscountShopOverrides(
  baseSettings: DiscountShopSettings,
  overrides?: DiscountShopOverrides,
): DiscountShopOverrides | undefined {
  if (!overrides) return undefined

  const normalized: Partial<DiscountShopOverrides> = {}

  for (const shopKey of discountShopKeys) {
    const override = overrides[shopKey]
    if (!override) continue

    const cleaned = discountShopSettingFields.reduce((acc, field) => {
      const value = override[field]
      if (value !== undefined && !isSameSettingValue(value, baseSettings[field])) {
        ;(acc as Record<string, string | boolean | QuantityDiscount | undefined>)[field] = value
      }
      return acc
    }, {} as Partial<DiscountShopOverride>)

    if (Object.keys(cleaned).length > 0) normalized[shopKey] = cleaned
  }

  return Object.keys(normalized).length > 0
    ? normalized as DiscountShopOverrides
    : undefined
}

type DiscountCalculationArgs = {
  combinationPrice: number
  idealoBestPrice?: number
  stock?: number
  settings: Pick<
    DiscountShopSettings,
    | 'discountRule'
    | 'discountRuleStock'
    | 'dynamicPrice'
    | 'dynamicDiscount'
    | 'dynamicDiscountStock'
    | 'dynamicMaxPrice'
    | 'dynamicMaxPriceStock'
  >
}

type ParsedDiscountRule = {
  value: number
  type: 'percent' | 'fixed'
}

const parseDiscountRule = (rule?: string): ParsedDiscountRule | undefined => {
  const sanitizedRule = String(rule ?? '').replace(',', '.').trim()
  if (!sanitizedRule) return undefined

  const value = Number(sanitizedRule.replace('%', ''))
  if (!Number.isFinite(value)) return undefined

  return {
    value,
    type: sanitizedRule.includes('%') ? 'percent' : 'fixed',
  }
}

const applyDiscountRuleToPrice = (basePrice: number, rule?: string): number => {
  const parsedRule = parseDiscountRule(rule)
  if (!parsedRule || typeof basePrice !== 'number') return basePrice

  const discountedPrice = parsedRule.type === 'percent'
    ? basePrice - (basePrice * parsedRule.value / 100)
    : basePrice - parsedRule.value

  return discountedPrice < 0 ? 0 : discountedPrice
}

const getQuantityDiscountApplications = (
  qty: number,
  quantityDiscount: QuantityDiscount,
) => {
  const requiredQuantity = normalizePositiveInteger(quantityDiscount.requiredQuantity)
  if (!requiredQuantity || qty < requiredQuantity) return 0

  if (quantityDiscount.applyTo === 'allItems') return 1

  const rewardQuantity = normalizePositiveInteger(quantityDiscount.rewardQuantity)
  if (!rewardQuantity) return 0

  return quantityDiscount.repeating === false
    ? 1
    : Math.floor(qty / requiredQuantity)
}

const getDiscountableRewardQuantity = (
  qty: number,
  quantityDiscount: QuantityDiscount,
) => {
  const applications = getQuantityDiscountApplications(qty, quantityDiscount)
  if (applications <= 0) return 0
  if (quantityDiscount.applyTo === 'allItems') return qty

  const rewardQuantity = normalizePositiveInteger(quantityDiscount.rewardQuantity)
  if (!rewardQuantity) return 0

  return Math.min(qty, applications * rewardQuantity)
}

export function applyQuantityDiscountToLinePrice(
  unitPrices: number[],
  quantityDiscount?: QuantityDiscount,
): number {
  const normalizedQuantityDiscount = normalizeQuantityDiscount(quantityDiscount)
  const linePrice = unitPrices.reduce((sum, unitPrice) => sum + unitPrice, 0)
  if (!normalizedQuantityDiscount || unitPrices.length === 0) return linePrice

  const rewardType = normalizedQuantityDiscount.rewardType
  const rewardQuantity = getDiscountableRewardQuantity(
    unitPrices.length,
    normalizedQuantityDiscount,
  )

  if (rewardQuantity <= 0) return linePrice

  if (rewardType === 'percent' && normalizedQuantityDiscount.applyTo === 'allItems') {
    return linePrice * (1 - (normalizedQuantityDiscount.rewardPercent || 0) / 100)
  }

  const rewardUnitPrices = [...unitPrices]
    .sort((a, b) => a - b)
    .slice(0, rewardQuantity)

  const rewardBaseTotal = rewardUnitPrices.reduce((sum, unitPrice) => sum + unitPrice, 0)
  const rewardDiscount = rewardType === 'freeItems'
    ? rewardBaseTotal
    : rewardBaseTotal * ((normalizedQuantityDiscount.rewardPercent || 0) / 100)

  return linePrice - rewardDiscount
}

export function calculateDiscountPrice({
  combinationPrice,
  idealoBestPrice,
  stock = 0,
  settings,
}: DiscountCalculationArgs): number {
  const hasIdealo = typeof idealoBestPrice === 'number' && idealoBestPrice > 0
  const isDynamic = settings.dynamicPrice === true && hasIdealo
  const isStockItem = stock > 0

  const regularPrice = isDynamic
    ? idealoBestPrice
    : combinationPrice

  const minimumDiscountRule = isStockItem
    ? settings.discountRuleStock
    : settings.discountRule

  const dynamicDiscountRule = isStockItem
    ? settings.dynamicDiscountStock
    : settings.dynamicDiscount

  const dynamicMaximumRule = isStockItem
    ? (settings.dynamicMaxPriceStock ?? settings.dynamicMaxPrice)
    : settings.dynamicMaxPrice

  let discountPrice = isDynamic
    ? applyDiscountRuleToPrice(regularPrice, dynamicDiscountRule)
    : applyDiscountRuleToPrice(regularPrice, minimumDiscountRule)

  if (isDynamic) {
    const minimumDynamicPrice = applyDiscountRuleToPrice(combinationPrice, minimumDiscountRule)
    if (minimumDynamicPrice < discountPrice) discountPrice = minimumDynamicPrice

    if (dynamicMaximumRule != null) {
      const maximumDynamicDiscountPrice = applyDiscountRuleToPrice(combinationPrice, dynamicMaximumRule)
      if (maximumDynamicDiscountPrice > discountPrice) {
        discountPrice = maximumDynamicDiscountPrice
      }
    }
  }

  return parseFloat(discountPrice.toFixed(2))
}

export function buildAppliedDiscountForShop(
  discount: DiscountConfigCarrier & DiscountDateCarrier,
  shopId: number,
  combinationPrice: number,
  idealoBestPrice?: number,
  stock?: number,
): AppliedShopDiscount {
  const settings = resolveDiscountShopSettings(discount, shopId)
  const stockQty = Math.max(0, Math.floor(stock ?? 0))
  const limitStockDiscountToStock = settings.limitStockDiscountToStock === true
    && stockQty > 0
  const discountPrice = calculateDiscountPrice({
    combinationPrice,
    idealoBestPrice,
    stock,
    settings,
  })
  const discountPriceAfterStock = limitStockDiscountToStock
    ? calculateDiscountPrice({
      combinationPrice,
      idealoBestPrice,
      stock: 0,
      settings,
    })
    : undefined
  const quantityDiscount = normalizeQuantityDiscount(settings.quantityDiscount)

  return {
    discountId: discount.id,
    discountPrice,
    ...(limitStockDiscountToStock ? {
      discountPriceAfterStock,
      stockDiscountLimit: stockQty,
      limitStockDiscountToStock: true,
    } : {}),
    ...(quantityDiscount ? { quantityDiscount } : {}),
    discountTitle: settings.slogan,
    discountType: settings.type,
    countdownVariant: settings.countdownVariant,
    showPercent: settings.showPercent,
    discountDateStart: discount.dateStart,
    discountDateEnd: discount.dateEnd,
    exportToIdealo: settings.exportToIdealo,
  }
}

export function getAppliedDiscountForShop(
  combination: CombinationDiscountCarrier,
  shopId: number,
): AppliedShopDiscount | undefined {
  const shopKey = toDiscountShopKey(shopId)
  const shopDiscount = shopKey ? combination.shopDiscounts?.[shopKey] : undefined
  const hasLegacyShop = Array.isArray(combination.discountShops)
    && combination.discountShops.indexOf(shopId) >= 0

  if (shopDiscount?.discountId && typeof shopDiscount.discountPrice === 'number') {
    return {
      ...shopDiscount,
      quantityDiscount: shopDiscount.quantityDiscount ?? combination.quantityDiscount,
    }
  }

  if (
    combination.discountId &&
    typeof combination.discountPrice === 'number' &&
    hasLegacyShop
  ) {
      return {
        discountId: combination.discountId,
        discountPrice: combination.discountPrice,
        discountPriceAfterStock: combination.discountPriceAfterStock,
        stockDiscountLimit: combination.stockDiscountLimit,
        limitStockDiscountToStock: combination.limitStockDiscountToStock,
        quantityDiscount: combination.quantityDiscount,
        discountTitle: combination.discountTitle,
        discountType: combination.discountType,
        countdownVariant: combination.discountCountdownVariant,
        showPercent: combination.discountShowPercent ?? true,
        discountDateStart: combination.discountDateStart,
        discountDateEnd: combination.discountDateEnd,
        exportToIdealo: true,
    }
  }

  return undefined
}

export function getDiscountPriceForPlatform(
  combination: CombinationDiscountCarrier,
  shopId: number,
  platform: 'store' | 'idealo' | 'google',
): number | undefined {
  const discount = getAppliedDiscountForShop(combination, shopId)
  if (!discount) return undefined

  if (platform === 'idealo' && discount.exportToIdealo === false) {
    return undefined
  }

  return discount.discountPrice
}

export function getEffectivePriceForPlatform(
  combination: CombinationDiscountCarrier,
  shopId: number,
  platform: 'store' | 'idealo' | 'google',
): number {
  return getDiscountPriceForPlatform(combination, shopId, platform) ?? combination.price
}
